import { queryOptions } from '@tanstack/react-query'

import { apiClient } from './client'
import type {
  AgeingLine,
  ArrearsAgeingQuery,
  ArrearsAgeingReport,
  BalanceSheetLine,
  BalanceSheetReport,
  BuildReportSnapshotRequest,
  CollectionLine,
  CollectionsRegisterQuery,
  CollectionsRegisterReport,
  DisbursementLine,
  DisbursementsRegisterQuery,
  DisbursementsRegisterReport,
  ExportableReport,
  ExportFormat,
  GroupHealthLine,
  GroupHealthQuery,
  GroupHealthReport,
  GroupPortfolioSummary,
  IncomeStatementLine,
  IncomeStatementReport,
  MoneyAmount,
  ParBucketView,
  ParLine,
  PerformanceLine,
  PerformanceQuery,
  PerformanceReport,
  PortfolioAtRiskQuery,
  PortfolioAtRiskReport,
  ReportBalanceSheetQuery,
  ReportDataSource,
  ReportDimension,
  ReportExportQuery,
  ReportHeader,
  ReportIncomeStatementQuery,
  ReportPage,
  ReportSnapshotListQuery,
  ReportSnapshotPage,
  ReportSnapshotStatus,
  ReportSnapshotSummary,
  ReportSnapshotType,
  ReportTrialBalanceLine,
  ReportTrialBalanceQuery,
  ReportTrialBalanceReport,
  StoredReport,
} from './reports-types'

/**
 * The reporting endpoints — eleven of them, across arrears and portfolio-at-risk, the collections
 * and disbursements registers, officer/branch/group performance, group health, the ledger's trial
 * balance re-served under this module, snapshots, and the CSV export.
 *
 * Every register and summary here reads a nightly rollup or a live query built for reporting,
 * never the transaction tables a teller or a collection sheet is posting against — the same split
 * `not-implemented.tsx`'s old placeholder promised: analytical reads never compete with the
 * operational ones for a lock.
 *
 * **Money arrives wrapped.** Unlike every other area module, this one's wire shape is
 * {@link MoneyAmount} — `{ minor, currency, decimalPlaces }` — rather than a bare `*MinorUnits`
 * field beside a `currencyCode` sibling. `toMoney` below is the one place that is unwrapped; every
 * mapper in this file calls it rather than touching `.minor` itself, so there is exactly one seam
 * to check if the wire shape ever changes. The trial balance section is the one exception — it is
 * the ledger's own report, re-served here in the ledger's flat convention, and is normalised the
 * same way `ledger-api.ts#trialBalanceQueryOptions` normalises it.
 *
 * **A ratio is null, not zero, whenever its denominator was zero.** `toNullableNumber` preserves
 * that distinction from the wire through to the screen; nothing in this file substitutes a 0 for a
 * ratio the server did not compute.
 *
 * **Nothing here trusts a payload's shape**, for the same reasons `ledger-api.ts` gives at its own
 * head: every identifier that is an `int64` on the wire (`branchId`, `loanOfficerUserId`,
 * `generatedByUserId`) is coerced through {@link toId}/{@link toNullableId}; every
 * `["integer","string"]` or `["number","string"]` member is coerced through {@link toNumber} or
 * {@link toNullableNumber}; every array is checked for being one before it is mapped. A customer id,
 * a group id and a loan id are genuine UUIDs on this module's wire — unlike a branch id — and pass
 * through unchanged; they were never a precision risk in the first place.
 */

/* ─── Query keys ──────────────────────────────────────────────────────────────────────────────── */

export const reportsKeys = {
  all: ['reports'] as const,
  arrearsAgeing: (query: ArrearsAgeingQuery) => [...reportsKeys.all, 'arrears-ageing', query] as const,
  portfolioAtRisk: (query: PortfolioAtRiskQuery) => [...reportsKeys.all, 'portfolio-at-risk', query] as const,
  collections: (query: CollectionsRegisterQuery) => [...reportsKeys.all, 'collections', query] as const,
  disbursements: (query: DisbursementsRegisterQuery) =>
    [...reportsKeys.all, 'disbursements', query] as const,
  performance: (query: PerformanceQuery) => [...reportsKeys.all, 'performance', query] as const,
  groupHealth: (query: GroupHealthQuery) => [...reportsKeys.all, 'group-health', query] as const,
  trialBalance: (query: ReportTrialBalanceQuery) => [...reportsKeys.all, 'trial-balance', query] as const,
  balanceSheet: (query: ReportBalanceSheetQuery) => [...reportsKeys.all, 'balance-sheet', query] as const,
  incomeStatement: (query: ReportIncomeStatementQuery) =>
    [...reportsKeys.all, 'income-statement', query] as const,
  snapshots: (query: ReportSnapshotListQuery) => [...reportsKeys.all, 'snapshots', 'list', query] as const,
  snapshot: (snapshotId: string) => [...reportsKeys.all, 'snapshots', 'detail', snapshotId] as const,
}

/* ─── Normalisation ───────────────────────────────────────────────────────────────────────────── */

/** An `int64` or a double as the API serialises it — numeric in practice, quoted when large. */
type WireNumber = number | string
type WireId = WireNumber

const toId = (value: WireId): string => String(value)

const toNullableId = (value: WireId | null | undefined): string | null =>
  value === null || value === undefined ? null : String(value)

const toNumber = (value: WireNumber): number => (typeof value === 'number' ? value : Number(value))

/** Preserves null rather than substituting 0 — the whole point of every nullable ratio here. */
const toNullableNumber = (value: WireNumber | null | undefined): number | null =>
  value === null || value === undefined ? null : toNumber(value)

/** The same, with a fallback, for a counter the server always sends and this client must not trust. */
const toCount = (value: WireNumber | null | undefined, fallback: number): number => {
  if (value === null || value === undefined) {
    return fallback
  }

  const parsed = toNumber(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const toArray = <T>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : [])

/** A branch identifier on the way out — numeric on the wire, string throughout this application. */
const outboundBranchId = (branchId: string | null | undefined): number | null =>
  branchId === null || branchId === undefined || branchId === '' ? null : Number(branchId)

/** Query-string parameters, with the members nobody chose removed — see `ledger-api.ts` for why. */
const params = (values: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  )

interface MoneyAmountPayload {
  minor: WireNumber
  currency: string
  decimalPlaces: WireNumber
}

const toMoney = (payload: MoneyAmountPayload): MoneyAmount => ({
  minor: toNumber(payload.minor),
  currency: payload.currency,
  decimalPlaces: toNumber(payload.decimalPlaces),
})

interface ReportHeaderPayload {
  reportName: string
  asOf: string
  fromDate: string | null
  toDate: string | null
  basis: ReportHeader['basis']
  generatedAtUtc: string
  currencyCode: string
  sources: ReportDataSource[] | null
  isComplete: boolean
  snapshotId: string | null
  classificationSchemeCode: string | null
}

const toReportHeader = (payload: ReportHeaderPayload): ReportHeader => ({
  ...payload,
  sources: toArray(payload.sources),
})

interface ReportPagePayload {
  page: WireNumber
  pageSize: WireNumber
  totalRows: WireNumber
  totalPages: WireNumber
}

function toReportPaging(
  payload: ReportPagePayload | null | undefined,
  requested: { page: number; pageSize: number },
): ReportPage {
  return {
    page: toCount(payload?.page, requested.page),
    pageSize: toCount(payload?.pageSize, requested.pageSize),
    totalRows: toCount(payload?.totalRows, 0),
    totalPages: toCount(payload?.totalPages, 1),
  }
}

/* ─── Portfolio at risk and arrears ageing ───────────────────────────────────────────────────── */

interface AgeingLinePayload {
  loanId: string
  loanAccountNumber: string
  roundNumber: WireNumber
  customerId: string
  customerName: string
  groupId: string | null
  groupName: string | null
  branchId: WireId
  loanOfficerUserId: WireId | null
  loanOfficerName: string | null
  loanProductName: string
  disbursedOn: string
  maturityDate: string
  asOf: string
  daysInArrears: WireNumber
  bucketCode: string
  bucketName: string
  bucketRange: string
  classCode: string | null
  classDiffersFromAgeing: boolean
  isRestructured: boolean
  principalOutstanding: MoneyAmountPayload
  totalOutstanding: MoneyAmountPayload
  amountInArrears: MoneyAmountPayload
  provisionRequired: MoneyAmountPayload
}

const toAgeingLine = (row: AgeingLinePayload): AgeingLine => ({
  loanId: row.loanId,
  loanAccountNumber: row.loanAccountNumber,
  roundNumber: toCount(row.roundNumber, 0),
  customerId: row.customerId,
  customerName: row.customerName,
  groupId: row.groupId,
  groupName: row.groupName,
  branchId: toId(row.branchId),
  loanOfficerUserId: toNullableId(row.loanOfficerUserId),
  loanOfficerName: row.loanOfficerName,
  loanProductName: row.loanProductName,
  disbursedOn: row.disbursedOn,
  maturityDate: row.maturityDate,
  asOf: row.asOf,
  daysInArrears: toCount(row.daysInArrears, 0),
  bucketCode: row.bucketCode,
  bucketName: row.bucketName,
  bucketRange: row.bucketRange,
  classCode: row.classCode,
  classDiffersFromAgeing: row.classDiffersFromAgeing,
  isRestructured: row.isRestructured,
  principalOutstanding: toMoney(row.principalOutstanding),
  totalOutstanding: toMoney(row.totalOutstanding),
  amountInArrears: toMoney(row.amountInArrears),
  provisionRequired: toMoney(row.provisionRequired),
})

interface ArrearsAgeingReportPayload {
  header: ReportHeaderPayload
  paging: ReportPagePayload
  rows: AgeingLinePayload[] | null
}

/**
 * Facility-level arrears ageing, worst first, as at a date. `arrearsOnly` defaults true on the
 * server, so it is sent only when explicitly turned off — see `params()` on why a default sent
 * anyway would just be noise in the request log.
 */
export const arrearsAgeingQueryOptions = (query: ArrearsAgeingQuery, enabled = true) => {
  const page = query.page ?? 1
  const pageSize = query.pageSize ?? 50

  return queryOptions({
    queryKey: reportsKeys.arrearsAgeing({ ...query, page, pageSize }),
    queryFn: async () => {
      const payload = (
        await apiClient.get<ArrearsAgeingReportPayload>('/reports/arrears-ageing', {
          params: params({
            asOf: query.asOf,
            arrearsOnly: query.arrearsOnly === false ? false : undefined,
            minimumDaysInArrears: query.minimumDaysInArrears,
            branchId: outboundBranchId(query.branchId),
            loanOfficerId: query.loanOfficerId,
            groupId: query.groupId,
            page,
            pageSize,
          }),
        })
      ).data

      return {
        header: toReportHeader(payload.header),
        paging: toReportPaging(payload.paging, { page, pageSize }),
        rows: toArray(payload.rows).map(toAgeingLine),
      } satisfies ArrearsAgeingReport
    },
    enabled,
    // A worklist that gains rows as postings land, not a figure someone has already quoted.
    staleTime: 30 * 1000,
  })
}

interface ParBucketViewPayload {
  classCode: string
  className: string
  range: string
  minDaysOverdue: WireNumber
  maxDaysOverdue: WireNumber | null
  rank: WireNumber
  isAtRisk: boolean
  isPerforming: boolean
  provisionRate: WireNumber
  facilityCount: WireNumber
  principalOutstanding: MoneyAmountPayload
  totalOutstanding: MoneyAmountPayload
  amountInArrears: MoneyAmountPayload
  provisionRequired: MoneyAmountPayload
  shareOfPortfolio: WireNumber | null
  cumulativePrincipalAtRisk: MoneyAmountPayload
  cumulativeParRatio: WireNumber | null
}

const toParBucket = (bucket: ParBucketViewPayload): ParBucketView => ({
  classCode: bucket.classCode,
  className: bucket.className,
  range: bucket.range,
  minDaysOverdue: toCount(bucket.minDaysOverdue, 0),
  maxDaysOverdue: bucket.maxDaysOverdue === null ? null : toNumber(bucket.maxDaysOverdue),
  rank: toCount(bucket.rank, 0),
  isAtRisk: bucket.isAtRisk,
  isPerforming: bucket.isPerforming,
  provisionRate: toNumber(bucket.provisionRate),
  facilityCount: toCount(bucket.facilityCount, 0),
  principalOutstanding: toMoney(bucket.principalOutstanding),
  totalOutstanding: toMoney(bucket.totalOutstanding),
  amountInArrears: toMoney(bucket.amountInArrears),
  provisionRequired: toMoney(bucket.provisionRequired),
  shareOfPortfolio: toNullableNumber(bucket.shareOfPortfolio),
  cumulativePrincipalAtRisk: toMoney(bucket.cumulativePrincipalAtRisk),
  cumulativeParRatio: toNullableNumber(bucket.cumulativeParRatio),
})

interface ParLinePayload {
  dimension: ReportDimension
  dimensionKey: string
  dimensionLabel: string
  facilityCount: WireNumber
  grossPortfolio: MoneyAmountPayload
  totalOutstanding: MoneyAmountPayload
  portfolioAtRisk: MoneyAmountPayload
  amountInArrears: MoneyAmountPayload
  provisionRequired: MoneyAmountPayload
  parRatio: WireNumber | null
  unclassifiedFacilityCount: WireNumber
  buckets: ParBucketViewPayload[] | null
}

const toParLine = (line: ParLinePayload): ParLine => ({
  dimension: line.dimension,
  dimensionKey: line.dimensionKey,
  dimensionLabel: line.dimensionLabel,
  facilityCount: toCount(line.facilityCount, 0),
  grossPortfolio: toMoney(line.grossPortfolio),
  totalOutstanding: toMoney(line.totalOutstanding),
  portfolioAtRisk: toMoney(line.portfolioAtRisk),
  amountInArrears: toMoney(line.amountInArrears),
  provisionRequired: toMoney(line.provisionRequired),
  parRatio: toNullableNumber(line.parRatio),
  unclassifiedFacilityCount: toCount(line.unclassifiedFacilityCount, 0),
  buckets: toArray(line.buckets).map(toParBucket),
})

interface PortfolioAtRiskReportPayload {
  header: ReportHeaderPayload
  dimension: ReportDimension
  total: ParLinePayload
  lines: ParLinePayload[] | null
}

/**
 * Portfolio at risk by ageing bucket, sliced by a dimension, as at a date.
 *
 * Refuses rather than answering with zeros when no classification scheme was in force for `asOf`
 * or the loan portfolio could not be read — the server's own description of the endpoint. A screen
 * reading this still has to check `header.isComplete`, because a partial answer (one branch's
 * portfolio unreadable, the rest fine) is not the same failure and is not refused.
 */
export const portfolioAtRiskQueryOptions = (query: PortfolioAtRiskQuery, enabled = true) =>
  queryOptions({
    queryKey: reportsKeys.portfolioAtRisk(query),
    queryFn: async () => {
      const payload = (
        await apiClient.get<PortfolioAtRiskReportPayload>('/reports/portfolio-at-risk', {
          params: params({
            asOf: query.asOf,
            dimension: query.dimension,
            branchId: outboundBranchId(query.branchId),
            loanOfficerId: query.loanOfficerId,
            groupId: query.groupId,
          }),
        })
      ).data

      return {
        header: toReportHeader(payload.header),
        dimension: payload.dimension,
        total: toParLine(payload.total),
        lines: toArray(payload.lines).map(toParLine),
      } satisfies PortfolioAtRiskReport
    },
    enabled,
    staleTime: 30 * 1000,
  })

/* ─── Collections register ────────────────────────────────────────────────────────────────────
 *
 * Counts `Repayment` and `EarlySettlement` transactions only. An instalment settled from a called
 * guarantee posts as `SecurityDepositOffset`, which this endpoint does not count as collected —
 * see the collections register screen's own note, which states this plainly rather than leaving a
 * reader to infer it from a total that looks a little short.
 */

interface CollectionLinePayload {
  transactionId: string
  transactionNumber: string
  valueDate: string
  postingDate: string
  branchId: WireId
  loanOfficerUserId: WireId | null
  loanOfficerName: string | null
  groupId: string | null
  groupName: string | null
  customerId: string
  customerName: string
  loanAccountNumber: string
  roundNumber: WireNumber
  amount: MoneyAmountPayload
  principal: MoneyAmountPayload
  interest: MoneyAmountPayload
  penalty: MoneyAmountPayload
  fees: MoneyAmountPayload
  isGuaranteeCallIn: boolean
  channel: string
  isAllocationBalanced: boolean
}

const toCollectionLine = (row: CollectionLinePayload): CollectionLine => ({
  transactionId: row.transactionId,
  transactionNumber: row.transactionNumber,
  valueDate: row.valueDate,
  postingDate: row.postingDate,
  branchId: toId(row.branchId),
  loanOfficerUserId: toNullableId(row.loanOfficerUserId),
  loanOfficerName: row.loanOfficerName,
  groupId: row.groupId,
  groupName: row.groupName,
  customerId: row.customerId,
  customerName: row.customerName,
  loanAccountNumber: row.loanAccountNumber,
  roundNumber: toCount(row.roundNumber, 0),
  amount: toMoney(row.amount),
  principal: toMoney(row.principal),
  interest: toMoney(row.interest),
  penalty: toMoney(row.penalty),
  fees: toMoney(row.fees),
  isGuaranteeCallIn: row.isGuaranteeCallIn,
  channel: row.channel,
  isAllocationBalanced: row.isAllocationBalanced,
})

interface CollectionsRegisterReportPayload {
  header: ReportHeaderPayload
  paging: ReportPagePayload
  totalCollected: MoneyAmountPayload
  totalPrincipal: MoneyAmountPayload
  totalInterest: MoneyAmountPayload
  totalPenalty: MoneyAmountPayload
  totalFees: MoneyAmountPayload
  totalGuaranteeCallIns: MoneyAmountPayload
  guaranteeCallInCount: WireNumber
  unbalancedRowCount: WireNumber
  rows: CollectionLinePayload[] | null
}

/** Repayments collected in a date range, by value date. */
export const collectionsRegisterQueryOptions = (query: CollectionsRegisterQuery, enabled = true) => {
  const page = query.page ?? 1
  const pageSize = query.pageSize ?? 50

  return queryOptions({
    queryKey: reportsKeys.collections({ ...query, page, pageSize }),
    queryFn: async () => {
      const payload = (
        await apiClient.get<CollectionsRegisterReportPayload>('/reports/collections', {
          params: params({
            fromDate: query.fromDate,
            toDate: query.toDate,
            branchId: outboundBranchId(query.branchId),
            loanOfficerId: query.loanOfficerId,
            groupId: query.groupId,
            page,
            pageSize,
          }),
        })
      ).data

      return {
        header: toReportHeader(payload.header),
        paging: toReportPaging(payload.paging, { page, pageSize }),
        totalCollected: toMoney(payload.totalCollected),
        totalPrincipal: toMoney(payload.totalPrincipal),
        totalInterest: toMoney(payload.totalInterest),
        totalPenalty: toMoney(payload.totalPenalty),
        totalFees: toMoney(payload.totalFees),
        totalGuaranteeCallIns: toMoney(payload.totalGuaranteeCallIns),
        guaranteeCallInCount: toCount(payload.guaranteeCallInCount, 0),
        unbalancedRowCount: toCount(payload.unbalancedRowCount, 0),
        rows: toArray(payload.rows).map(toCollectionLine),
      } satisfies CollectionsRegisterReport
    },
    enabled,
    staleTime: 30 * 1000,
  })
}

/* ─── Disbursements register ──────────────────────────────────────────────────────────────────── */

interface DisbursementLinePayload {
  disbursementId: string
  transactionNumber: string
  valueDate: string
  postingDate: string
  branchId: WireId
  loanOfficerUserId: WireId | null
  loanOfficerName: string | null
  groupId: string | null
  groupName: string | null
  customerId: string
  customerName: string
  loanAccountNumber: string
  roundNumber: WireNumber
  loanProductName: string
  principal: MoneyAmountPayload
  feesDeducted: MoneyAmountPayload
  securityDepositDeducted: MoneyAmountPayload
  netPaidOut: MoneyAmountPayload
  instalments: WireNumber
  maturityDate: string
}

const toDisbursementLine = (row: DisbursementLinePayload): DisbursementLine => ({
  disbursementId: row.disbursementId,
  transactionNumber: row.transactionNumber,
  valueDate: row.valueDate,
  postingDate: row.postingDate,
  branchId: toId(row.branchId),
  loanOfficerUserId: toNullableId(row.loanOfficerUserId),
  loanOfficerName: row.loanOfficerName,
  groupId: row.groupId,
  groupName: row.groupName,
  customerId: row.customerId,
  customerName: row.customerName,
  loanAccountNumber: row.loanAccountNumber,
  roundNumber: toCount(row.roundNumber, 0),
  loanProductName: row.loanProductName,
  principal: toMoney(row.principal),
  feesDeducted: toMoney(row.feesDeducted),
  securityDepositDeducted: toMoney(row.securityDepositDeducted),
  netPaidOut: toMoney(row.netPaidOut),
  instalments: toCount(row.instalments, 0),
  maturityDate: row.maturityDate,
})

interface DisbursementsRegisterReportPayload {
  header: ReportHeaderPayload
  paging: ReportPagePayload
  totalPrincipal: MoneyAmountPayload
  totalFeesDeducted: MoneyAmountPayload
  totalSecurityDepositDeducted: MoneyAmountPayload
  totalNetPaidOut: MoneyAmountPayload
  rows: DisbursementLinePayload[] | null
}

/** Rounds disbursed in a date range, with the net actually paid out after fees and any deposit. */
export const disbursementsRegisterQueryOptions = (query: DisbursementsRegisterQuery, enabled = true) => {
  const page = query.page ?? 1
  const pageSize = query.pageSize ?? 50

  return queryOptions({
    queryKey: reportsKeys.disbursements({ ...query, page, pageSize }),
    queryFn: async () => {
      const payload = (
        await apiClient.get<DisbursementsRegisterReportPayload>('/reports/disbursements', {
          params: params({
            fromDate: query.fromDate,
            toDate: query.toDate,
            branchId: outboundBranchId(query.branchId),
            loanOfficerId: query.loanOfficerId,
            groupId: query.groupId,
            page,
            pageSize,
          }),
        })
      ).data

      return {
        header: toReportHeader(payload.header),
        paging: toReportPaging(payload.paging, { page, pageSize }),
        totalPrincipal: toMoney(payload.totalPrincipal),
        totalFeesDeducted: toMoney(payload.totalFeesDeducted),
        totalSecurityDepositDeducted: toMoney(payload.totalSecurityDepositDeducted),
        totalNetPaidOut: toMoney(payload.totalNetPaidOut),
        rows: toArray(payload.rows).map(toDisbursementLine),
      } satisfies DisbursementsRegisterReport
    },
    enabled,
    staleTime: 30 * 1000,
  })
}

/* ─── Officer, branch and group performance ──────────────────────────────────────────────────── */

interface PerformanceLinePayload {
  dimension: ReportDimension
  dimensionKey: string
  dimensionLabel: string
  branchId: WireId | null
  asOf: string
  fromDate: string
  toDate: string
  activeFacilities: WireNumber
  borrowerCount: WireNumber
  groupCount: WireNumber
  portfolioOutstanding: MoneyAmountPayload
  totalOutstanding: MoneyAmountPayload
  portfolioAtRisk: MoneyAmountPayload
  amountInArrears: MoneyAmountPayload
  provisionRequired: MoneyAmountPayload
  atRiskRatio: WireNumber | null
  nonPerformingFacilities: WireNumber
  nonPerformingOutstanding: MoneyAmountPayload
  nonPerformingRatio: WireNumber | null
  unclassifiedFacilityCount: WireNumber
  expectedInPeriod: MoneyAmountPayload
  collectedInPeriod: MoneyAmountPayload
  collectedPrincipal: MoneyAmountPayload
  collectedInterest: MoneyAmountPayload
  collectedPenalty: MoneyAmountPayload
  collectedFees: MoneyAmountPayload
  collectionCount: WireNumber
  collectionRate: WireNumber | null
  disbursedInPeriod: MoneyAmountPayload
  disbursementCount: WireNumber
}

const toPerformanceLine = (line: PerformanceLinePayload): PerformanceLine => ({
  dimension: line.dimension,
  dimensionKey: line.dimensionKey,
  dimensionLabel: line.dimensionLabel,
  branchId: toNullableId(line.branchId),
  asOf: line.asOf,
  fromDate: line.fromDate,
  toDate: line.toDate,
  activeFacilities: toCount(line.activeFacilities, 0),
  borrowerCount: toCount(line.borrowerCount, 0),
  groupCount: toCount(line.groupCount, 0),
  portfolioOutstanding: toMoney(line.portfolioOutstanding),
  totalOutstanding: toMoney(line.totalOutstanding),
  portfolioAtRisk: toMoney(line.portfolioAtRisk),
  amountInArrears: toMoney(line.amountInArrears),
  provisionRequired: toMoney(line.provisionRequired),
  atRiskRatio: toNullableNumber(line.atRiskRatio),
  nonPerformingFacilities: toCount(line.nonPerformingFacilities, 0),
  nonPerformingOutstanding: toMoney(line.nonPerformingOutstanding),
  nonPerformingRatio: toNullableNumber(line.nonPerformingRatio),
  unclassifiedFacilityCount: toCount(line.unclassifiedFacilityCount, 0),
  expectedInPeriod: toMoney(line.expectedInPeriod),
  collectedInPeriod: toMoney(line.collectedInPeriod),
  collectedPrincipal: toMoney(line.collectedPrincipal),
  collectedInterest: toMoney(line.collectedInterest),
  collectedPenalty: toMoney(line.collectedPenalty),
  collectedFees: toMoney(line.collectedFees),
  collectionCount: toCount(line.collectionCount, 0),
  collectionRate: toNullableNumber(line.collectionRate),
  disbursedInPeriod: toMoney(line.disbursedInPeriod),
  disbursementCount: toCount(line.disbursementCount, 0),
})

interface PerformanceReportPayload {
  header: ReportHeaderPayload
  dimension: ReportDimension
  total: PerformanceLinePayload
  lines: PerformanceLinePayload[] | null
}

/**
 * Officer, branch or group performance over a period.
 *
 * Portfolio figures are as at `asOf`; collection and disbursement figures cover `fromDate` to
 * `toDate` — two different questions asked of the same dimension member, and the report states
 * both ranges rather than one shared one. Every rate is null where its denominator was zero, so a
 * member with nothing falling due is not reported as having collected nothing.
 */
export const performanceQueryOptions = (query: PerformanceQuery, enabled = true) =>
  queryOptions({
    queryKey: reportsKeys.performance(query),
    queryFn: async () => {
      const payload = (
        await apiClient.get<PerformanceReportPayload>('/reports/performance', {
          params: params({
            asOf: query.asOf,
            fromDate: query.fromDate,
            toDate: query.toDate,
            dimension: query.dimension,
            branchId: outboundBranchId(query.branchId),
            loanOfficerId: query.loanOfficerId,
            groupId: query.groupId,
          }),
        })
      ).data

      return {
        header: toReportHeader(payload.header),
        dimension: payload.dimension,
        total: toPerformanceLine(payload.total),
        lines: toArray(payload.lines).map(toPerformanceLine),
      } satisfies PerformanceReport
    },
    enabled,
    staleTime: 30 * 1000,
  })

/* ─── Group health ────────────────────────────────────────────────────────────────────────────── */

interface GroupPortfolioSummaryPayload {
  facilityCount: WireNumber
  borrowerCount: WireNumber
  portfolioOutstanding: MoneyAmountPayload
  portfolioAtRisk: MoneyAmountPayload
  amountInArrears: MoneyAmountPayload
  provisionRequired: MoneyAmountPayload
  parRatio: WireNumber | null
  facilitiesInArrears: WireNumber
  maxDaysInArrears: WireNumber
}

const toGroupPortfolio = (payload: GroupPortfolioSummaryPayload): GroupPortfolioSummary => ({
  facilityCount: toCount(payload.facilityCount, 0),
  borrowerCount: toCount(payload.borrowerCount, 0),
  portfolioOutstanding: toMoney(payload.portfolioOutstanding),
  portfolioAtRisk: toMoney(payload.portfolioAtRisk),
  amountInArrears: toMoney(payload.amountInArrears),
  provisionRequired: toMoney(payload.provisionRequired),
  parRatio: toNullableNumber(payload.parRatio),
  facilitiesInArrears: toCount(payload.facilitiesInArrears, 0),
  maxDaysInArrears: toCount(payload.maxDaysInArrears, 0),
})

interface GroupHealthLinePayload {
  groupId: string
  groupCode: string
  groupName: string
  branchId: WireId
  loanOfficerUserId: WireId | null
  loanOfficerName: string | null
  status: string
  activeMemberCount: WireNumber
  currentRoundNumber: WireNumber
  healthOutcome: string
  healthScore: WireNumber | null
  qualifies: boolean
  qualifyThreshold: WireNumber
  reason: string | null
  isProvisional: boolean
  provisionalUntil: string | null
  healthComputedAtUtc: string | null
  portfolio: GroupPortfolioSummaryPayload | null
}

const toGroupHealthLine = (row: GroupHealthLinePayload): GroupHealthLine => ({
  groupId: row.groupId,
  groupCode: row.groupCode,
  groupName: row.groupName,
  branchId: toId(row.branchId),
  loanOfficerUserId: toNullableId(row.loanOfficerUserId),
  loanOfficerName: row.loanOfficerName,
  status: row.status,
  activeMemberCount: toCount(row.activeMemberCount, 0),
  currentRoundNumber: toCount(row.currentRoundNumber, 0),
  healthOutcome: row.healthOutcome,
  healthScore: toNullableNumber(row.healthScore),
  qualifies: row.qualifies,
  qualifyThreshold: toNumber(row.qualifyThreshold),
  reason: row.reason,
  isProvisional: row.isProvisional,
  provisionalUntil: row.provisionalUntil,
  healthComputedAtUtc: row.healthComputedAtUtc,
  portfolio: row.portfolio === null ? null : toGroupPortfolio(row.portfolio),
})

interface GroupHealthReportPayload {
  header: ReportHeaderPayload
  paging: ReportPagePayload
  groupsWithoutPortfolioData: WireNumber
  rows: GroupHealthLinePayload[] | null
}

/**
 * Group standing and portfolio quality, as at a date.
 *
 * Groups whose loan book could not be read carry a null `portfolio` rather than a row of zeros —
 * `groupsWithoutPortfolioData` on the report counts them, so the gap is visible on the summary
 * rather than only discoverable by scanning every row for a dash.
 */
export const groupHealthQueryOptions = (query: GroupHealthQuery, enabled = true) => {
  const page = query.page ?? 1
  const pageSize = query.pageSize ?? 50

  return queryOptions({
    queryKey: reportsKeys.groupHealth({ ...query, page, pageSize }),
    queryFn: async () => {
      const payload = (
        await apiClient.get<GroupHealthReportPayload>('/reports/group-health', {
          params: params({
            asOf: query.asOf,
            branchId: outboundBranchId(query.branchId),
            loanOfficerId: query.loanOfficerId,
            groupId: query.groupId,
            page,
            pageSize,
          }),
        })
      ).data

      return {
        header: toReportHeader(payload.header),
        paging: toReportPaging(payload.paging, { page, pageSize }),
        groupsWithoutPortfolioData: toCount(payload.groupsWithoutPortfolioData, 0),
        rows: toArray(payload.rows).map(toGroupHealthLine),
      } satisfies GroupHealthReport
    },
    enabled,
    staleTime: 30 * 1000,
  })
}

/* ─── Trial balance ───────────────────────────────────────────────────────────────────────────
 *
 * The reporting module's own trial balance — a signed position per account, in this module's own
 * `MoneyAmount` convention, read through `reports.trialbalance.view` rather than the ledger
 * module's own wider permission. See the note at the head of `reports-types.ts` on why the response
 * types here carry a `Report` prefix rather than the bare names the API's own OpenAPI document
 * currently (and wrongly) resolves to the ledger module's differently shaped type of the same name.
 */

interface ReportTrialBalanceLinePayload {
  glAccountCode: string
  accountName: string
  accountType: string
  level: WireNumber
  isPostable: boolean
  debit: MoneyAmountPayload
  credit: MoneyAmountPayload
}

const toReportTrialBalanceLine = (line: ReportTrialBalanceLinePayload): ReportTrialBalanceLine => ({
  glAccountCode: line.glAccountCode,
  accountName: line.accountName,
  accountType: line.accountType,
  level: toCount(line.level, 0),
  isPostable: line.isPostable,
  debit: toMoney(line.debit),
  credit: toMoney(line.credit),
})

interface ReportTrialBalanceReportPayload {
  header: ReportHeaderPayload
  branchId: WireId | null
  totalDebit: MoneyAmountPayload
  totalCredit: MoneyAmountPayload
  isBalanced: boolean
  rows: ReportTrialBalanceLinePayload[] | null
}

/**
 * The reporting module's trial balance as at a business date, read from the ledger and never
 * recomputed here. Point-in-time rather than the ranged `/ledger/trial-balance` this application
 * already screens under Ledger → Trial balance, and a simpler shape than that screen's own read —
 * the same figures, asked for through the reporting module's own gate.
 */
export const reportTrialBalanceQueryOptions = (query: ReportTrialBalanceQuery, enabled = true) =>
  queryOptions({
    queryKey: reportsKeys.trialBalance(query),
    queryFn: async () => {
      const payload = (
        await apiClient.get<ReportTrialBalanceReportPayload>('/reports/trial-balance', {
          params: params({ asOf: query.asOf, branchId: outboundBranchId(query.branchId) }),
        })
      ).data

      return {
        header: toReportHeader(payload.header),
        branchId: toNullableId(payload.branchId),
        totalDebit: toMoney(payload.totalDebit),
        totalCredit: toMoney(payload.totalCredit),
        isBalanced: payload.isBalanced,
        rows: toArray(payload.rows).map(toReportTrialBalanceLine),
      } satisfies ReportTrialBalanceReport
    },
    enabled: enabled && query.asOf !== '',
    staleTime: 0,
  })

/* ─── Balance sheet and income statement ─────────────────────────────────────────────────────────
 *
 * Both read the same trial balance figures the ledger already holds — the balance sheet is that
 * trial balance's asset, liability and equity rows split into two sides; the income statement is
 * the ledger's account movement over a period. Neither carries a paging block on the wire: each is
 * one coherent statement, not a listing.
 */

interface BalanceSheetLinePayload {
  glAccountCode: string
  accountName: string
  accountType: string
  level: WireNumber
  isPostable: boolean
  amount: MoneyAmountPayload
}

const toBalanceSheetLine = (line: BalanceSheetLinePayload): BalanceSheetLine => ({
  glAccountCode: line.glAccountCode,
  accountName: line.accountName,
  accountType: line.accountType,
  level: toCount(line.level, 0),
  isPostable: line.isPostable,
  amount: toMoney(line.amount),
})

interface BalanceSheetReportPayload {
  header: ReportHeaderPayload
  branchId: WireId | null
  totalAssets: MoneyAmountPayload
  totalLiabilities: MoneyAmountPayload
  totalEquity: MoneyAmountPayload
  totalLiabilitiesAndEquity: MoneyAmountPayload
  isBalanced: boolean
  assetLines: BalanceSheetLinePayload[] | null
  liabilityAndEquityLines: BalanceSheetLinePayload[] | null
}

/**
 * Assets against liabilities and equity, as at a business date. Read from the same trial balance
 * the ledger already holds, restricted to the asset, liability and equity classes — never a second
 * summation.
 */
export const reportBalanceSheetQueryOptions = (query: ReportBalanceSheetQuery, enabled = true) =>
  queryOptions({
    queryKey: reportsKeys.balanceSheet(query),
    queryFn: async () => {
      const payload = (
        await apiClient.get<BalanceSheetReportPayload>('/reports/balance-sheet', {
          params: params({ asOf: query.asOf, branchId: outboundBranchId(query.branchId) }),
        })
      ).data

      return {
        header: toReportHeader(payload.header),
        branchId: toNullableId(payload.branchId),
        totalAssets: toMoney(payload.totalAssets),
        totalLiabilities: toMoney(payload.totalLiabilities),
        totalEquity: toMoney(payload.totalEquity),
        totalLiabilitiesAndEquity: toMoney(payload.totalLiabilitiesAndEquity),
        isBalanced: payload.isBalanced,
        assetLines: toArray(payload.assetLines).map(toBalanceSheetLine),
        liabilityAndEquityLines: toArray(payload.liabilityAndEquityLines).map(toBalanceSheetLine),
      } satisfies BalanceSheetReport
    },
    enabled: enabled && query.asOf !== '',
    staleTime: 0,
  })

interface IncomeStatementLinePayload {
  glAccountCode: string
  accountName: string
  accountType: string
  level: WireNumber
  isPostable: boolean
  amount: MoneyAmountPayload
}

const toIncomeStatementLine = (line: IncomeStatementLinePayload): IncomeStatementLine => ({
  glAccountCode: line.glAccountCode,
  accountName: line.accountName,
  accountType: line.accountType,
  level: toCount(line.level, 0),
  isPostable: line.isPostable,
  amount: toMoney(line.amount),
})

interface IncomeStatementReportPayload {
  header: ReportHeaderPayload
  branchId: WireId | null
  totalIncome: MoneyAmountPayload
  totalExpenses: MoneyAmountPayload
  netProfit: MoneyAmountPayload
  incomeLines: IncomeStatementLinePayload[] | null
  expenseLines: IncomeStatementLinePayload[] | null
}

/**
 * Income against expense over a period, read from the ledger's daily balance rollup summed across
 * the range — a movement, not a position, so it is never confused with the trial balance's own
 * closing figures.
 */
export const reportIncomeStatementQueryOptions = (query: ReportIncomeStatementQuery, enabled = true) =>
  queryOptions({
    queryKey: reportsKeys.incomeStatement(query),
    queryFn: async () => {
      const payload = (
        await apiClient.get<IncomeStatementReportPayload>('/reports/income-statement', {
          params: params({
            fromDate: query.fromDate,
            toDate: query.toDate,
            branchId: outboundBranchId(query.branchId),
          }),
        })
      ).data

      return {
        header: toReportHeader(payload.header),
        branchId: toNullableId(payload.branchId),
        totalIncome: toMoney(payload.totalIncome),
        totalExpenses: toMoney(payload.totalExpenses),
        netProfit: toMoney(payload.netProfit),
        incomeLines: toArray(payload.incomeLines).map(toIncomeStatementLine),
        expenseLines: toArray(payload.expenseLines).map(toIncomeStatementLine),
      } satisfies IncomeStatementReport
    },
    enabled,
    staleTime: 0,
  })

/* ─── Snapshots ───────────────────────────────────────────────────────────────────────────────── */

interface ReportSnapshotSummaryPayload {
  snapshotId: string
  reportType: ReportSnapshotType
  status: ReportSnapshotStatus
  dimension: ReportDimension
  asOf: string
  fromDate: string | null
  toDate: string | null
  branchId: WireId | null
  loanOfficerUserId: WireId | null
  groupId: string | null
  currencyCode: string
  classificationSchemeCode: string | null
  rowCount: WireNumber
  generatedAtUtc: string
  generatedByUserId: WireId | null
  supersededBy: string | null
  sources: ReportDataSource[] | null
}

const toSnapshotSummary = (payload: ReportSnapshotSummaryPayload): ReportSnapshotSummary => ({
  snapshotId: payload.snapshotId,
  reportType: payload.reportType,
  status: payload.status,
  dimension: payload.dimension,
  asOf: payload.asOf,
  fromDate: payload.fromDate,
  toDate: payload.toDate,
  branchId: toNullableId(payload.branchId),
  loanOfficerUserId: toNullableId(payload.loanOfficerUserId),
  groupId: payload.groupId,
  currencyCode: payload.currencyCode,
  classificationSchemeCode: payload.classificationSchemeCode,
  rowCount: toCount(payload.rowCount, 0),
  generatedAtUtc: payload.generatedAtUtc,
  generatedByUserId: toNullableId(payload.generatedByUserId),
  supersededBy: payload.supersededBy,
  sources: toArray(payload.sources),
})

interface ReportSnapshotPagePayload {
  page: WireNumber
  pageSize: WireNumber
  totalRows: WireNumber
  totalPages: WireNumber
  rows: ReportSnapshotSummaryPayload[] | null
}

/**
 * Stored report snapshots the caller may see, newest first. Superseded snapshots stay listed
 * alongside live ones and name the snapshot that replaced them, so a restated figure stays
 * findable next to the figure it restated. Not wired to a screen in this pass.
 */
export const reportSnapshotsQueryOptions = (query: ReportSnapshotListQuery, enabled = true) => {
  const page = query.page ?? 1
  const pageSize = query.pageSize ?? 50

  return queryOptions({
    queryKey: reportsKeys.snapshots({ ...query, page, pageSize }),
    queryFn: async () => {
      const payload = (
        await apiClient.get<ReportSnapshotPagePayload>('/reports/snapshots', {
          params: params({
            reportType: query.reportType,
            status: query.status,
            fromAsOf: query.fromAsOf,
            toAsOf: query.toAsOf,
            branchId: outboundBranchId(query.branchId),
            page,
            pageSize,
          }),
        })
      ).data

      return {
        page: toCount(payload.page, page),
        pageSize: toCount(payload.pageSize, pageSize),
        totalRows: toCount(payload.totalRows, 0),
        totalPages: toCount(payload.totalPages, 1),
        rows: toArray(payload.rows).map(toSnapshotSummary),
      } satisfies ReportSnapshotPage
    },
    enabled,
    staleTime: 30 * 1000,
  })
}

/**
 * Freezes a report as at a date, so its figures cannot move afterwards. Building a second snapshot
 * for the same report, date, period and scope is refused unless `rebuild` is set, which supersedes
 * the first and keeps both. Not wired to a screen in this pass — see the reports index.
 */
export async function buildReportSnapshot(
  request: BuildReportSnapshotRequest,
): Promise<ReportSnapshotSummary> {
  const payload = (
    await apiClient.post<ReportSnapshotSummaryPayload>('/reports/snapshots', request)
  ).data

  return toSnapshotSummary(payload)
}

interface StoredReportPayload {
  snapshot: ReportSnapshotSummaryPayload
  payloadHash: string
  payloadJson: string
}

/**
 * A stored report, with its figures exactly as they were published. Cached indefinitely: a
 * snapshot is hash-verified on the server and refused if the stored payload no longer matches it,
 * so re-reading one can only ever produce the same answer or a refusal.
 */
export const reportSnapshotQueryOptions = (snapshotId: string, enabled = true) =>
  queryOptions({
    queryKey: reportsKeys.snapshot(snapshotId),
    queryFn: async () => {
      const payload = (
        await apiClient.get<StoredReportPayload>(
          `/reports/snapshots/${encodeURIComponent(snapshotId)}`,
        )
      ).data

      return {
        snapshot: toSnapshotSummary(payload.snapshot),
        payloadHash: payload.payloadHash,
        payloadJson: payload.payloadJson,
      } satisfies StoredReport
    },
    enabled: enabled && snapshotId !== '',
    staleTime: Number.POSITIVE_INFINITY,
  })

/* ─── Export: CSV, XLSX and PDF ──────────────────────────────────────────────────────────────────
 *
 * One function for every format this endpoint answers with, because the three differ only in
 * `format` — the report, the range, the dimension and the scope are the same parameters for all
 * three, narrowed exactly as the screen they were exported from was narrowed. `format` defaults to
 * `'Csv'` server-side when omitted; this client always sends it explicitly so the request states
 * plainly what it is asking to come back as.
 */

/** File extension for a format, used only for the fallback name when the server sends none. */
const EXPORT_EXTENSION: Record<ExportFormat, string> = { Csv: 'csv', Xlsx: 'xlsx', Pdf: 'pdf' }

/**
 * Exports one page of a report as CSV, a real workbook, or a print-ready PDF.
 *
 * Proper auth here means nothing more than using the one axios instance every read in this
 * application goes through: `apiClient`'s request interceptor attaches the bearer token and the
 * branch header exactly as it does for a JSON request, so there is no second, unauthenticated path
 * to this endpoint. What this deliberately does not do is stitch several pages into one file —
 * exporting more than a screenful needs `reports.export.bulk`, and every export button in this
 * application downloads the same page the operator is looking at rather than trying to work around
 * that server-side limit.
 */
export async function exportReport(
  query: ReportExportQuery & { format: ExportFormat },
): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.get<Blob>('/reports/export', {
    params: params({
      report: query.report,
      format: query.format,
      asOf: query.asOf,
      fromDate: query.fromDate,
      toDate: query.toDate,
      dimension: query.dimension,
      arrearsOnly: query.arrearsOnly === false ? false : undefined,
      minimumDaysInArrears: query.minimumDaysInArrears,
      branchId: outboundBranchId(query.branchId),
      loanOfficerId: query.loanOfficerId,
      groupId: query.groupId,
      page: query.page,
      pageSize: query.pageSize,
    }),
    responseType: 'blob',
  })

  return { blob: response.data, filename: filenameFromExport(response.headers, query.report, query.format) }
}

/** The server names the file in `Content-Disposition`; a report code plus the format's extension is the fallback. */
function filenameFromExport(
  headers: Record<string, unknown>,
  report: ExportableReport,
  format: ExportFormat,
): string {
  const disposition = headers['content-disposition']

  if (typeof disposition === 'string') {
    const match = /filename\*?=(?:UTF-8'')?"?([^;"]+)/i.exec(disposition)
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1])
      } catch {
        return match[1]
      }
    }
  }

  return `${report}.${EXPORT_EXTENSION[format]}`
}
